import React from 'react';

const BLOCK_TAGS = new Set([
    'address',
    'article',
    'aside',
    'blockquote',
    'div',
    'dl',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'hr',
    'li',
    'main',
    'nav',
    'ol',
    'p',
    'section',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'ul'
]);

const normalizePlainText = (value) => {
    if (!value || typeof value !== 'string') {
        return '';
    }

    return value
        .replace(/\u00a0/g, ' ')
        .replace(/[\t\f\v]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const extractRenderableSegments = (html, fallbackText = '') => {
    if (!html || typeof html !== 'string' || typeof DOMParser === 'undefined') {
        return normalizePlainText(fallbackText)
            ? [{ type: 'text', content: normalizePlainText(fallbackText) }]
            : [];
    }

    const documentRoot = new DOMParser().parseFromString(html, 'text/html');
    const segments = [];
    let textBuffer = '';

    const flushTextBuffer = () => {
        const normalized = normalizePlainText(textBuffer);
        if (normalized) {
            segments.push({ type: 'text', content: normalized });
        }
        textBuffer = '';
    };

    const appendText = (text) => {
        if (!text) {
            return;
        }

        textBuffer += text;
    };

    const appendLineBreak = (count = 1) => {
        textBuffer += '\n'.repeat(count);
    };

    const walkNode = (node) => {
        if (!node) {
            return;
        }

        if (node.nodeType === Node.TEXT_NODE) {
            appendText(node.textContent || '');
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const tagName = node.nodeName.toLowerCase();

        if (tagName === 'br') {
            appendLineBreak(1);
            return;
        }

        if (tagName === 'pre' || tagName === 'code') {
            flushTextBuffer();
            const codeContent = normalizePlainText(node.textContent || '');
            if (codeContent) {
                segments.push({ type: 'code', content: codeContent });
            }
            return;
        }

        const isBlock = BLOCK_TAGS.has(tagName);
        if (isBlock && textBuffer && !textBuffer.endsWith('\n')) {
            appendLineBreak(tagName === 'td' || tagName === 'th' ? 1 : 2);
        }

        if (tagName === 'li') {
            appendText('• ');
        }

        Array.from(node.childNodes || []).forEach(walkNode);

        if (isBlock) {
            appendLineBreak(tagName === 'td' || tagName === 'th' ? 1 : 2);
        }
    };

    Array.from(documentRoot.body?.childNodes || []).forEach(walkNode);
    flushTextBuffer();

    if (segments.length === 0) {
        const normalizedFallback = normalizePlainText(fallbackText);
        if (normalizedFallback) {
            return [{ type: 'text', content: normalizedFallback }];
        }
    }

    return segments;
};

export const formatEmailPreviewDate = (dateValue) => {
    if (!dateValue || typeof dateValue !== 'string') {
        return '';
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return dateValue;
    }

    return parsedDate.toString();
};

const buildCcBccValue = (preview = {}) => {
    const parts = [];

    if (preview.cc) {
        parts.push(`CC: ${preview.cc}`);
    }

    if (preview.bcc) {
        parts.push(`BCC: ${preview.bcc}`);
    }

    return parts.join(' | ');
};

export const EmailPreviewContent = ({
    preview,
    fallbackTitle,
    containerStyle = {},
    bodyStyle = {},
    dividerStyle = {},
    textClassName = 'file-preview-cont'
}) => {
    if (!preview) {
        return null;
    }

    const localizedDate = formatEmailPreviewDate(preview.date);
    const ccBccValue = buildCcBccValue(preview);
    const renderableSegments = extractRenderableSegments(preview.html, preview.text);

    return (
        <div
            style={{
                padding: '15px',
                fontSize: '12px',
                lineHeight: '1.45',
                backgroundColor: 'var(--secondary-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                textAlign: 'left',
                ...containerStyle
            }}
        >
            <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', textAlign: 'left' }}>
                    {preview.subject || fallbackTitle}
                </div>
                {preview.from && <p style={{ margin: '4px 0', textAlign: 'left' }}><strong>From:</strong> {preview.from}</p>}
                {preview.to && <p style={{ margin: '4px 0', textAlign: 'left' }}><strong>To:</strong> {preview.to}</p>}
                {ccBccValue && <p style={{ margin: '4px 0', textAlign: 'left' }}><strong>CC/BCC:</strong> {ccBccValue}</p>}
                {localizedDate && <p style={{ margin: '4px 0', textAlign: 'left' }}><strong>Date:</strong> {localizedDate}</p>}
            </div>
            <div style={{ height: '1px', backgroundColor: 'var(--border-color)', margin: '12px 0', ...dividerStyle }} />
            {renderableSegments.length > 0 ? (
                <div
                    style={{
                        color: 'var(--text-color)',
                        lineHeight: 1.45,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        textAlign: 'left',
                        ...bodyStyle
                    }}
                >
                    {renderableSegments.map((segment, index) => (
                        segment.type === 'code' ? (
                            <pre
                                key={`segment-${index}`}
                                className={textClassName}
                                style={{
                                    margin: index === renderableSegments.length - 1 ? 0 : '0 0 12px 0',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '12px',
                                    textAlign: 'left',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    backgroundColor: 'var(--main-color)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px',
                                    padding: '10px'
                                }}
                            >
                                {segment.content}
                            </pre>
                        ) : (
                            <pre
                                key={`segment-${index}`}
                                className={textClassName}
                                style={{
                                    margin: index === renderableSegments.length - 1 ? 0 : '0 0 12px 0',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '12px',
                                    textAlign: 'left'
                                }}
                            >
                                {segment.content}
                            </pre>
                        )
                    ))}
                </div>
            ) : (
                <pre className={textClassName} style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', textAlign: 'left', ...bodyStyle }}>
                    {preview.text || 'No email body found.'}
                </pre>
            )}
        </div>
    );
};

export default EmailPreviewContent;
